import React, { useState } from 'react';
import { Plus, Filter, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { format } from 'date-fns';

const Bidding: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: bids, loading, error, refetch } = useApi(
    () => apiService.getBidRequests({ status: statusFilter === 'all' ? undefined : statusFilter }),
    [statusFilter]
  );

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { class: 'badge-pending', icon: Clock };
      case 'approved':
        return { class: 'badge-active', icon: CheckCircle };
      case 'rejected':
        return { class: 'badge-expired', icon: XCircle };
      default:
        return { class: 'badge-inactive', icon: Clock };
    }
  };

  const [processingBidId, setProcessingBidId] = useState<number | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);

  const handleProcessBid = async (bidId: number) => {
    setProcessingBidId(bidId);
    setProcessError(null);
    try {
      await apiService.processBidRequest(bidId);
      refetch();
    } catch (error: any) {
      console.error('Failed to process bid:', error);
      setProcessError(error.message || 'Failed to process bid request');
    } finally {
      setProcessingBidId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Bidding</h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage crew bidding requests and preferences
          </p>
        </div>
        <button
          onClick={() => alert('New bid request form coming soon')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Bid Request
        </button>
      </div>

      {/* Filters */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-48"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <button
            onClick={() => alert('Advanced filters coming soon')}
            className="btn-secondary flex items-center gap-2"
          >
            <Filter className="h-5 w-5" />
            More Filters
          </button>
        </div>
      </div>

      {/* Error Toast */}
      {processError && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-400 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          <span>{processError}</span>
          <button onClick={() => setProcessError(null)} className="ml-2 hover:text-red-300">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Bids List */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="glass-card p-6 text-center text-red-400">{error.message}</div>
      ) : (
        <div className="space-y-4">
          {bids?.map((bid) => {
            const statusInfo = getStatusBadge(bid.status);
            const StatusIcon = statusInfo.icon;

            return (
              <div key={bid.id} className="glass-card p-6 transition-all hover:border-sky-500/30">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-white">
                        Bid Request #{bid.id}
                      </h3>
                      <span className={`flex items-center gap-1 ${statusInfo.class}`}>
                        <StatusIcon className="h-3 w-3" />
                        {bid.status}
                      </span>
                      <span className="badge-sky">Priority {bid.priority}</span>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <p className="text-xs text-gray-400">Bid Type</p>
                        <p className="mt-1 text-sm font-medium text-white">{bid.bid_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Roster Period</p>
                        <p className="mt-1 text-sm text-white">
                          {format(new Date(bid.roster_period_start), 'MMM dd')} -{' '}
                          {format(new Date(bid.roster_period_end), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Submitted</p>
                        <p className="mt-1 text-sm text-white">
                          {format(new Date(bid.submitted_at), 'MMM dd, yyyy HH:mm')}
                        </p>
                      </div>
                    </div>

                    {bid.preferences && Object.keys(bid.preferences).length > 0 && (
                      <div className="mt-4 rounded-lg border border-gray-800 bg-gray-900/30 p-3">
                        <p className="mb-2 text-xs font-medium text-gray-400">Preferences</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(bid.preferences).map(([key, value]) => (
                            <span key={key} className="badge-inactive text-xs">
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {bid.notes && (
                      <div className="mt-3">
                        <p className="text-sm text-gray-400">{bid.notes}</p>
                      </div>
                    )}
                  </div>

                  {bid.status === 'pending' && (
                    <div className="ml-6 flex gap-2">
                      <button
                        onClick={() => handleProcessBid(bid.id)}
                        disabled={processingBidId === bid.id}
                        className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
                      >
                        {processingBidId === bid.id ? 'Processing...' : 'Process'}
                      </button>
                      <button
                        onClick={() => alert('Review functionality coming soon')}
                        className="btn-secondary px-4 py-2 text-sm"
                      >
                        Review
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {bids?.length === 0 && (
            <div className="glass-card p-12 text-center">
              <p className="text-gray-400">No bidding requests found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Bidding;
